import React, { useState } from "react";
import { Typography, Box } from "@mui/material";
import PasswordIcon from "@mui/icons-material/Password";
import PinIcon from "@mui/icons-material/Pin";
import SyncIcon from "@mui/icons-material/Sync";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PrimaryButton from "../../../widgets/Buttons/PrimaryButton";
import SecondaryButton from "../../../widgets/Buttons/SecondaryButton";
import ActionCard from "../../../widgets/ActionCard/ActionCard";
import CustomModal from "../../../widgets/Modal/CustomModal";
import ErrorSnackbar from "../../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../../widgets/Snackbar/SuccessSnackbar";
import { useGetOtp } from "../../../hooks/useGetOtp";
import { useUpdatePassword } from "../../../hooks/useUpdatePassword";
import { useResetOtp } from "../../../hooks/useResetOtp";
import { useUnblockUser } from "../../../hooks/useUnblockUser";
import PasswordTextField from "../../../widgets/Textfield/PasswordTextfield";
import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from "../../../constants/constants";

const OtpManagementCard = ({ phone, features, studentData, verticalInfo , branchId, branchName}) => {
  const {
    mutate: getOtp,
    isPending: isGetOtpPending,
    isLoading: isGetOtpLoading,
  } = useGetOtp();
  const {
    mutate: updatePassword,
    isPending: isUpdatePasswordPending,
    isLoading: isUpdatePasswordLoading,
  } = useUpdatePassword();
  const isUpdatingPassword = isUpdatePasswordPending || isUpdatePasswordLoading;
  const isGettingOtp = isGetOtpPending || isGetOtpLoading;

  const {
    mutate: resetOtp,
    isPending: isResetOtpPending,
    isLoading: isResetOtpLoading,
  } = useResetOtp();
  const isResettingOtp = isResetOtpPending || isResetOtpLoading;

  const {
    mutate: unblockUser,
    isPending: isUnblockUserPending,
    isLoading: isUnblockUserLoading,
  } = useUnblockUser();
  const isUnblockingUser = isUnblockUserPending || isUnblockUserLoading;

  const [otpModal, setOtpModal] = useState({ open: false, code: "" });
  const [passwordModal, setPasswordModal] = useState({
    open: false,
    password: "",
    error: "",
  });

  const validatePassword = (val) => {
    if (!val) return "Password is required.";
    if (val.length < MIN_PASSWORD_LENGTH) return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (val.length > MAX_PASSWORD_LENGTH) return `Password must not exceed ${MAX_PASSWORD_LENGTH} characters.`;
    if (/\s/.test(val)) return "Password cannot contain spaces.";
    if (val.includes("..") || val.includes("__"))
      return "Password cannot contain consecutive dots or underscores.";
    return "";
  };
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    actionText: "",
  });
  const [errorSnackbar, setErrorSnackbar] = useState({
    open: false,
    message: "",
  });
  const [successSnackbar, setSuccessSnackbar] = useState({
    open: false,
    message: "",
  });
  const handleUpdatePassword = () => {
    const err = validatePassword(passwordModal.password);
    if (err) {
      setPasswordModal((prev) => ({ ...prev, error: err }));
      return;
    }
    updatePassword(
      {
        admission_id: studentData?.admission_number,
        password: passwordModal.password,
        branch: branchId,
        product_id: verticalInfo?.productId,
        tenant_id: verticalInfo?.tenantId,
      },
      {
        onSuccess: () => {
          setSuccessSnackbar({
            open: true,
            message: "Password updated successfully.",
          });

          setPasswordModal({
            open: false,
            password: "",
            error: "",
          });
        },
        onError: (err) => {
          setErrorSnackbar({
            open: true,
            message: err.message || "Failed to update password.",
          });
        },
      },
    );
  };

  const handleGetOtp = () => {
    getOtp(
      {
        phone,
        isd_code: "+91",
        tenant_id: verticalInfo?.tenantId || "1",
        product_id: verticalInfo?.productId || "300",
      },
      {
        onSuccess: (res) => {
          const code =
            res?.otpCode ||
            res?.data?.otpCode ||
            res?.otp_code ||
            res?.data?.otp_code;
          if (code) {
            setOtpModal({ open: true, code });
            setSuccessSnackbar({
              open: true,
              message: "OTP fetched successfully.",
            });
          } else {
            setErrorSnackbar({ open: true, message: "OTP not found." });
          }
        },
        onError: (err) => {
          setErrorSnackbar({
            open: true,
            message: err.message || "Failed to fetch OTP.",
          });
        },
      },
    );
  };

  const handleConfirmYes = () => {
    const action = confirmModal.actionText;
    setConfirmModal({ open: false, actionText: "" });

    if (action === "Reset OTP") {
      resetOtp(
        { phone, isd_code: "+91" },
        {
          onSuccess: () => {
            setSuccessSnackbar({
              open: true,
              message: "OTP reset successfully.",
            });
          },
          onError: (err) => {
            setErrorSnackbar({
              open: true,
              message: err.message || "Failed to reset OTP.",
            });
          },
        },
      );
    } else if (action === "Unblock User") {
      unblockUser(
        {
          admission_id: studentData?.admission_number,
          productId: verticalInfo?.productId,
        },
        {
          onSuccess: () => {
            setSuccessSnackbar({
              open: true,
              message: "User unblocked successfully.",
            });
          },
          onError: (err) => {
            setErrorSnackbar({
              open: true,
              message: err.message || "Failed to unblock user.",
            });
          },
        },
      );
    }
  };

  return (
    <>
      <ActionCard title="OTP & Password Management">
        {features.getOtp && (
          <PrimaryButton
            startIcon={<PinIcon />}
            onClick={handleGetOtp}
            isLoading={isGettingOtp}
            sx={{ height: "35px" }}
          >
            {isGettingOtp ? "Fetching..." : "Get OTP"}
          </PrimaryButton>
        )}
        {features.resetOtp && (
          <PrimaryButton
            startIcon={<SyncIcon />}
            onClick={() =>
              setConfirmModal({ open: true, actionText: "Reset OTP" })
            }
            sx={{ height: "35px" }}
            isLoading={isResettingOtp}
          >
            {isResettingOtp ? "Resetting..." : "Reset OTP"}
          </PrimaryButton>
        )}
        {features.unblock && (
          <PrimaryButton
            startIcon={<LockOpenIcon />}
            onClick={() =>
              setConfirmModal({ open: true, actionText: "Unblock User" })
            }
            sx={{ height: "35px" }}
            isLoading={isUnblockingUser}
          >
            {isUnblockingUser ? "Unblocking..." : "Unblock User"}
          </PrimaryButton>
        )}
        {features.updatePassword && (
          <PrimaryButton
            startIcon={<PasswordIcon />}
            onClick={() =>
              setPasswordModal({
                open: true,
                password: "",
                error: "",
              })
            }
            sx={{ height: "35px" }}
            isLoading={isUpdatingPassword}
          >
            {isUpdatingPassword ? "Updating..." : "Update Password"}
          </PrimaryButton>
        )}
      </ActionCard>

      <CustomModal
        open={otpModal.open}
        onClose={() => setOtpModal({ open: false, code: "" })}
        title="Generated OTP"
        maxWidth="xs"
        actions={
          <SecondaryButton
            onClick={() => setOtpModal({ open: false, code: "" })}
          >
            Close
          </SecondaryButton>
        }
      >
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            bgcolor: "#f8fafc",
            borderRadius: 2,
            border: "1px dashed #cbd5e1",
          }}
        >
          <Typography
            variant="h3"
            sx={{ letterSpacing: "8px", fontWeight: "bold", color: "#1d4ed8" }}
          >
            {otpModal.code}
          </Typography>
        </Box>
      </CustomModal>

      <CustomModal
        open={passwordModal.open}
        onClose={() =>
          setPasswordModal({ open: false, password: "", error: "" })
        }
        title="Update Password"
        maxWidth="sm"
        actions={
          <>
            <SecondaryButton
              onClick={() =>
                setPasswordModal({ open: false, password: "", error: "" })
              }
            >
              Cancel
            </SecondaryButton>

            <PrimaryButton
              onClick={handleUpdatePassword}
              isLoading={isUpdatingPassword}
            >
              {isUpdatingPassword ? "Updating..." : "Update Password"}
            </PrimaryButton>
          </>
        }
      >
        <Box sx={{ pt: 1, pb: 1 }}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              color: "#64748b",
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            Password must be 6–64 characters, no spaces, and no consecutive dots
            (<code>..</code>) or underscores (<code>__</code>).
          </Typography>
          <PasswordTextField
            id="update-password"
            name="password"
            label="New Password"
            value={passwordModal.password}
            error={!!passwordModal.error}
            helperText={passwordModal.error || " "}
            onChange={(e) => {
              const val = e.target.value;
              setPasswordModal((prev) => ({
                ...prev,
                password: val,
                error: validatePassword(val),
              }));
            }}
            fullWidth
          />
        </Box>
      </CustomModal>

      <CustomModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, actionText: "" })}
        title="Confirm Action"
        maxWidth="xs"
        actions={
          <>
            <SecondaryButton
              onClick={() => setConfirmModal({ open: false, actionText: "" })}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleConfirmYes}>Confirm</PrimaryButton>
          </>
        }
      >
        <Typography sx={{ color: "#334155", fontSize: "1rem" }}>
          Are you sure you want to proceed with{" "}
          <strong>{confirmModal.actionText}</strong>?
        </Typography>
      </CustomModal>

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={() => setErrorSnackbar({ open: false, message: "" })}
      />
      <SuccessSnackbar
        open={successSnackbar.open}
        message={successSnackbar.message}
        onClose={() => setSuccessSnackbar({ open: false, message: "" })}
      />
    </>
  );
};

export default OtpManagementCard;
